import React, { useState, useEffect, memo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  CircularProgress,
  Box,
  Typography,
  useMediaQuery,
  Card,
  CardContent,
  Divider,
  Chip,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonIcon from "@mui/icons-material/Person";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import EditIcon from "@mui/icons-material/Edit";
import JobActionDialogs from "./JobActionDialogs";
import JobEditDialog from "./JobEditDialog";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import configServ from "../../services/config";
import dayjs from "dayjs";
import CardHeadMenu from "../../Components/Card/CardHeadMenu/CardHeadMenu";

const STATUS_OPTIONS = ["new", "in_progress", "completed", "cancelled"];

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "new":        return "info";
    case "in_progress": return "warning";
    case "completed":  return "success";
    case "cancelled":  return "error";
    default:           return "default";
  }
};

const getPriorityColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case "high":   return "error";
    case "medium": return "warning";
    case "low":    return "success";
    default:       return "default";
  }
};

const JobDetailsTable = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { company_id, user_id, is_super, is_admin } = useSelector(
    (state) => state.GlobalVariables
  );

  // --- List state ---
  const [jobList, setJobList]       = useState([]);
  const [loader, setLoader]         = useState(false);
  const [page, setPage]             = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal]           = useState(0);

  // --- Filter state ---
  const [filterDate, setFilterDate]     = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterStaff, setFilterStaff]   = useState("");
  const [staffList, setStaffList]       = useState([]);

  // --- View dialog state ---
  const [dialogState, setDialogState] = useState({
    open: false,
    type: "",
    data: null,
  });

  // --- Edit dialog state ---
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  // ── Load staff dropdown ──────────────────────────────────────────────────
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const res = await configServ.getStaffActiveCustomer({
          company_id,
          user_id,
          is_admin: Boolean(is_super || is_admin),
        });
        if (res?.status) setStaffList(res.data || []);
      } catch (e) {
        console.error("Staff fetch error:", e);
      }
    };
    if (company_id) fetchStaff();
  }, [company_id]);

  // ── Fetch jobs ────────────────────────────────────────────────────────────
  const fetchJobDetails = async () => {
    setLoader(true);
    try {
      const payload = {
        company_id,
        user_id,
        is_admin: Boolean(is_super || is_admin),
        page: page + 1,
        per_page: rowsPerPage,
      };

      if (filterDate)   payload.date     = filterDate;
      if (filterStatus) payload.status   = filterStatus;
      if (filterStaff)  payload.staff_id = filterStaff;

      const res = await configServ.getJobDetails(payload);

      if (res?.status) {
        setJobList(res.data || []);
        setTotal(res.pagination?.total || 0);
      } else {
        setJobList([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      setJobList([]);
      setTotal(0);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (company_id) fetchJobDetails();
  }, [page, rowsPerPage, company_id]);

  const handleApplyFilters = () => {
    setPage(0);
    fetchJobDetails();
  };

  const handleClearFilters = () => {
    setFilterDate("");
    setFilterStatus("");
    setFilterStaff("");
    setPage(0);
  };

  // Re-fetch when filters are cleared
  useEffect(() => {
    if (filterDate === "" && filterStatus === "" && filterStaff === "") {
      if (company_id) fetchJobDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDate, filterStatus, filterStaff]);

  // ── Dialog helpers ────────────────────────────────────────────────────────
  const handleOpenAction = (type, row) => {
    setDialogState({ open: true, type, data: row });
  };

  const handleCloseAction = () => {
    setDialogState({ open: false, type: "", data: null });
  };

  const handleOpenEdit = (row) => {
    setEditData(row);
    setEditDialogOpen(true);
  };

  const handleCloseEdit = () => {
    setEditDialogOpen(false);
    setEditData(null);
  };

  const handleUpdated = () => {
    fetchJobDetails();
  };

  // ── Filter bar ────────────────────────────────────────────────────────────
  const FilterBar = () => (
    <Box
      sx={{
        width: "100%",
        //backgroundColor: "#e3f3fc",
        p: 0,
        borderRadius: 2,
        mb: 2,
        mx: 1,
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            type="date"
            label="Date"
            size="small"
            fullWidth
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ backgroundColor: "white" }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
              sx={{ backgroundColor: "white" }}
            >
              <MenuItem value="">All</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s.replace("_", " ").replace(/^\w/, (c) => c.toUpperCase())}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Staff</InputLabel>
            <Select
              value={filterStaff}
              label="Staff"
              onChange={(e) => setFilterStaff(e.target.value)}
              sx={{ backgroundColor: "white" }}
            >
              <MenuItem value="">All</MenuItem>
              {staffList.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.contact_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              size="medium"
              onClick={handleApplyFilters}
              sx={{ flexGrow: 1 }}
            >
              Search
            </Button>
            <Tooltip title="Clear filters">
              <IconButton
                size="small"
                onClick={handleClearFilters}
                sx={{
                  backgroundColor: "secondary.main",
                  color: "white",
                  "&:hover": { backgroundColor: "secondary.dark" },
                }}
              >
                <FilterAltOffIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  // ── Action buttons (shared) ───────────────────────────────────────────────
  const ActionButtons = ({ row }) => (
    <Box display="flex" justifyContent="center" gap={0.5}>
      <Tooltip title="Edit Job">
        <IconButton
          size="small"
          onClick={() => handleOpenEdit(row)}
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            "&:hover": { backgroundColor: "primary.dark" },
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Job Execution Details">
        <IconButton
          size="small"
          color="primary"
          onClick={() => handleOpenAction("general", row)}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Job Master Info">
        <IconButton
          size="small"
          sx={{ color: "#2e7d32" }}
          onClick={() => handleOpenAction("master", row)}
        >
          <AssignmentIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Staff & Site Info">
        <IconButton
          size="small"
          sx={{ color: "#ed6c02" }}
          onClick={() => handleOpenAction("staff", row)}
        >
          <PersonIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // ── Mobile card view ──────────────────────────────────────────────────────
  const MobileView = () => (
    <Box sx={{ p: 1 }}>
      {jobList.length > 0 ? (
        jobList.map((row) => (
          <Card
            key={row.id}
            sx={{
              mb: 2,
              borderRadius: "12px",
              boxShadow: 2,
              borderLeft: `5px solid ${
                row.is_sla_breached ? "#d32f2f" : "#1976d2"
              }`,
            }}
          >
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {row.job_name}
                </Typography>
                <Chip
                  size="small"
                  label={row.status?.replace("_", " ")}
                  color={getStatusColor(row.status)}
                  sx={{ textTransform: "capitalize", fontWeight: "bold" }}
                />
              </Box>

              <Typography variant="caption" color="text.secondary">
                {row.job_code} · {row.job_category_name}
              </Typography>

              <Divider sx={{ my: 1 }} />

              <Typography variant="body2">
                <strong>Staff:</strong> {row.staff_name || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Customer:</strong> {row.customer_name || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong>{" "}
                {row.job_date
                  ? dayjs(row.job_date).format("DD-MM-YYYY")
                  : "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Priority:</strong>{" "}
                <Chip
                  size="small"
                  label={row.priority || "N/A"}
                  color={getPriorityColor(row.priority)}
                  sx={{ textTransform: "capitalize", height: 18, fontSize: 11 }}
                />
              </Typography>
              {row.is_sla_breached === 1 && (
                <Chip
                  size="small"
                  label="SLA Breached"
                  color="error"
                  sx={{ mt: 0.5 }}
                />
              )}

              <Box display="flex" justifyContent="flex-end" mt={1}>
                <ActionButtons row={row} />
              </Box>
            </CardContent>
          </Card>
        ))
      ) : (
        <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
          No Data Found
        </Typography>
      )}
    </Box>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <CardHeadMenu
        prefix={"Na"}
        suffix={"Na"}
        title={"JOB DETAILS"}
        showCreate="false"
      />

      <FilterBar />

      <Box sx={{ width: "100%", px: isMobile ? 0 : 0, pb: 2 }}>
        {loader ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="40vh"
          >
            <CircularProgress />
          </Box>
        ) : isMobile ? (
          <MobileView />
        ) : (
          <Paper
            sx={{
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#1976d2" }}>
                    {[
                      "#",
                      "Job Name",
                      "Staff",
                      "Customer",
                      "Date",
                      "Priority",
                      "Status",
                      "Action",
                    ].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          color: "#fff",
                          fontWeight: "bold",
                          whiteSpace: "nowrap",
                        }}
                        align={h === "Action" ? "center" : "left"}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {jobList.length > 0 ? (
                    jobList.map((row, index) => (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{
                          backgroundColor:
                            row.is_sla_breached === 1
                              ? "rgba(211,47,47,0.06)"
                              : "inherit",
                        }}
                      >
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {row.job_name}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {row.staff_name || "N/A"}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {row.customer_name || "N/A"}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {row.job_date
                            ? dayjs(row.job_date).format("DD-MM-YYYY")
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.priority || "—"}
                            color={getPriorityColor(row.priority)}
                            sx={{
                              textTransform: "capitalize",
                              fontWeight: "bold",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.status?.replace("_", " ") || "—"}
                            color={getStatusColor(row.status)}
                            sx={{
                              textTransform: "capitalize",
                              fontWeight: "bold",
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <ActionButtons row={row} />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No Data Found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ mt: 1 }}
        />
      </Box>

      <JobActionDialogs
        open={dialogState.open}
        type={dialogState.type}
        data={dialogState.data}
        onClose={handleCloseAction}
      />

      <JobEditDialog
        open={editDialogOpen}
        data={editData}
        staffList={staffList}
        onClose={handleCloseEdit}
        onUpdated={handleUpdated}
      />
    </>
  );
};

export default memo(JobDetailsTable);
